import { CharacterGender } from "@1games-ru/region-shared/dist/enums/character/character";
import { DocumentData } from "@1games-ru/region-shared/dist/types/documents/documentData";
import { useMemo } from "react";
import clsx from "clsx";

function Field({
  label,
  labelHorizontalPosition = "left",
  labelVerticalPosition = "center",
  value,
  className,
  classNameValue,
  classNameLabel,
}: {
  label?: string;
  labelHorizontalPosition?: "left" | "right";
  labelVerticalPosition?: "center" | "bottom";
  value: string;
  className?: string;
  classNameValue?: string;
  classNameLabel?: string;
}) {
  const labelComponent = label ? (
    <div className={clsx("text-[#7E2C35] text-14 font-medium", classNameLabel)}>{label}</div>
  ) : null;

  if (labelVerticalPosition === "center") {
    return (
      <div className={clsx("flex items-end gap-2.5", className)}>
        {labelHorizontalPosition === "left" && labelComponent}
        <div
          className={clsx(
            "border-b border-b-[#7E2C35] text-center text-dark-800 text-15 uppercase",
            classNameValue,
          )}
        >
          {value}
        </div>

        {labelHorizontalPosition === "right" && labelComponent}
      </div>
    );
  }

  if (labelVerticalPosition === "bottom") {
    return (
      <div
        className={clsx(
          "flex flex-col gap-1",
          labelHorizontalPosition === "left" ? "items-start" : "items-end",
          className,
        )}
      >
        <div
          className={clsx(
            "border-b border-b-[#7E2C35] text-center text-dark-800 text-15 uppercase",
            classNameValue,
          )}
        >
          {value}
        </div>
        {labelComponent}
      </div>
    );
  }
}

export default function MilitaryID({
  docNumber,
  issuedAt,
  issuer,
  owner,
  expiresAt,
}: DocumentData) {
  const maritalStatus = useMemo(() => {
    if (owner.maritalStatus) {
      return owner.sex === CharacterGender.Female ? "замужем" : "женат";
    }
    return "холост";
  }, [owner]);
  return (
    <div
      className={clsx(
        "w-[47rem] h-[32.125rem] grid grid-cols-2 items-stretch bg-[#F7F9FF]",
        "rounded-[1.25rem] overflow-clip",
      )}
      style={{
        backgroundImage: "url(./svg/documents/military-id.webp)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <div className="p-[1.875rem] flex flex-col gap-5">
        <div className="mt-auto">
          <div className="text-gray-800 text-16 text-center font-medium uppercase tracking-[0.3rem] mt-auto whitespace-nowrap">
            ВОЕННЫЙ БИЛЕТ РЕГИОНА
          </div>
          <div className="text-[#7E2C35] text-16 text-center font-medium uppercase tracking-[0.187rem] mt-1.5">
            {docNumber}
          </div>
        </div>
        <Field label="Фамилия" value={owner.lastName} className="w-full" classNameValue="grow" />
        <Field label="Имя" value={owner.firstName} className="w-full" classNameValue="grow" />
        <Field
          label="Дата Рождения"
          value={owner.dateOfBirth}
          className="w-full"
          classNameValue="grow"
        />
        <Field
          label="Выдан"
          value="ВОЕННЫМ КОМИССАРИАТОМ"
          className="w-full"
          classNameValue="grow"
        />
        <Field value="гор. САНКТ-ПЕТЕРБУРГ" className="w-full" classNameValue="grow" />
        <div className="flex items-end gap-5">
          <div className="relative shrink-0 bg-white">
            <img
              src={owner.photo}
              alt=""
              className="w-[6.25rem] h-[7.5rem] border border-[#7E2C35] pointer-events-none"
            />
            <div
              className="absolute -top-[0.3rem] left-0 right-0  h-[0.625rem]"
              style={{
                backgroundImage: "url(./svg/documents/red-photo-border-g.svg)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
              }}
            />
            <div
              className="absolute -top-[0.3rem] -left-[0.3rem] h-[6.25rem] w-[0.625rem]"
              style={{
                backgroundImage: "url(./svg/documents/red-photo-border-v.svg)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
              }}
            />
          </div>
          <div className="grow flex flex-col gap-[2.625rem]">
            <Field
              value={issuedAt}
              label="дата выдачи"
              labelHorizontalPosition="right"
              labelVerticalPosition="bottom"
              className="w-full"
              classNameValue="w-full"
            />
            <div className="relative w-full">
              {owner?.signature && (
                <div
                  className="absolute h-[4rem] w-full -top-[2rem] left-0"
                  style={{
                    backgroundImage: `url(${owner.signature})`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              )}
              <Field
                value=""
                label="подпись"
                labelHorizontalPosition="right"
                className="w-full"
                classNameValue="w-full"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center p-[1.875rem]">
        <div className={clsx("text-[#7E2C35] text-16 font-medium uppercase tracking-[0.187rem]")}>
          {docNumber}
        </div>
        <Field
          label="1. Место Рождения"
          value="ГОР. САНКТ-ПЕТЕРБУРГ"
          className="w-full mt-[2rem]"
          classNameLabel="w-[4.25rem] break-words"
          classNameValue="grow"
        />
        <Field
          label="2. Семейное положение"
          value={maritalStatus}
          className="w-full mt-5"
          classNameValue="grow"
        />
        <Field
          label="3. Звание"
          value={owner.rankName}
          className="w-full mt-5"
          classNameValue="grow"
        />
        <Field label="4. Воинская служба" value="-" className="w-full mt-5" classNameValue="grow" />
        <Field
          value={expiresAt || "по настоящее время"}
          className="w-full mt-5"
          classNameValue="grow lowercase"
        />
        <div className="flex items-end gap-5 w-full mt-auto">
          <img
            src="./svg/documents/seal-military-commissariat.webp"
            alt=""
            className="z-1 w-[6.25rem] aspect-square pointer-events-none"
          />
          <div className="grow flex flex-col gap-[2.625rem]">
            <Field
              value={issuer.name}
              label="военный комиссар"
              labelHorizontalPosition="right"
              labelVerticalPosition="bottom"
              className="w-full"
              classNameValue="w-full"
            />

            <div className="relative w-full">
              <img
                src="./svg/dark-signature.svg" // TODO: {issuer.signature}
                className="absolute h-[4rem] w-full -top-[2rem] left-0"
                alt=""
              />
              <Field
                value=""
                label="подпись"
                labelHorizontalPosition="right"
                className="w-full"
                classNameValue="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
